import { useTranslation } from 'react-i18next';
import { useAreasQuery, useIterationsQuery, usePlatformCatalogQuery, useTagsQuery } from '@platform/state/api';
import { useLocation } from 'react-router-dom';
import { appRoutes } from '../../shell/appRoutes';
import { recordTypeTotal } from '../../shell/recordTypeTotal';
import { useWorkspaceCatalog } from '../../shell/useWorkspaceCatalog';
import { useWorkspaceMeta } from '../../shell/useWorkspaceMeta';
import type { ISystemMenuGroup } from '../system/ISystemMenuGroup';
import { ScopeSwitch } from '../system/ScopeSwitch';
import { SystemMenu } from '../system/SystemMenu';
import { useCustomizationScope } from '../system/useCustomizationScope';
import { globalTemplateCount } from './globalTemplateCount';
import type { ICustomizeMenuProps } from './ICustomizeMenuProps';
import { linkTypeInverse } from './linkTypeKeys';

/**
 * Ліве меню «Налаштування» (прототип v4 sysGroups cust, SP-01/02): «Налаштування» з рівнем у підзаголовку («Робочий простір · простір» /
 * «Глобально · кожен робочий простір») і перемикачем рівня; робочий простір — «Модулі» (встановлені, форми — лічильники як у прототипі:
 * реалізовані типи записів; конструктор, розміщення полів),
 * «Класифікатори» (шляхи напрямів та ітерацій, теги), «Зв’язки»; глобально — каталог модулів і форми (шаблони глобального каталогу —
 * «169», ADR-027), розміщення полів, типи зв’язків.
 */
export function CustomizeMenu({ ws }: ICustomizeMenuProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { workspace } = useWorkspaceMeta(ws);
  const state = useCustomizationScope(ws);
  const global = state.scope === 'global';
  const { catalog } = useWorkspaceCatalog(ws);
  const { data: areas } = useAreasQuery(ws, { skip: !ws || global });
  const { data: iterations } = useIterationsQuery(ws, { skip: !ws || global });
  const { data: tags } = useTagsQuery(ws, { skip: !ws || global });
  const { data: platform } = usePlatformCatalogQuery(undefined, { skip: !global || !state.canGlobal });
  const modules = global ? globalTemplateCount(platform) : recordTypeTotal(catalog);
  const s = (key: string) => t(`sysPages.sections.${key}`);
  const at = (section: string) => appRoutes.customize(section);
  const onBuilder = pathname.includes('/customize/builder');
  const groups: ISystemMenuGroup[] = [
    { id: 'top', items: [{ key: 'overview', label: t('sysPages.menu.overview'), to: appRoutes.customize(), end: true }] },
    { id: 'modules', title: t('sysPages.menu.modules'), items: [
      { key: 'modules', label: s(global ? 'catalog' : 'installed'), to: at('modules'), count: modules },
      { key: 'forms', label: s('forms'), to: at('forms'), count: modules },
      ...(global ? [] : [{ key: 'builder', label: s('builder'), to: at('builder'), active: onBuilder || undefined }]),
      { key: 'layout', label: s('layout'), to: at('layout') },
    ] },
    ...(global ? [] : [{ id: 'classifiers', title: t('sysPages.menu.classifiers'), items: [
      { key: 'areas', label: s('areas'), to: at('areas'), count: areas?.length },
      { key: 'iterations', label: s('iterations'), to: at('iterations'), count: iterations?.length },
      { key: 'tags', label: s('tags'), to: at('tags'), count: tags?.length },
    ] }]),
    { id: 'relations', title: t('sysPages.menu.relations'), items: [{ key: 'links', label: s('links'), to: at('links'), count: Object.keys(linkTypeInverse).length }] },
  ];
  const sub = global ? t('sysPages.custMenuGlobal') : t('sysPages.custMenuWs', { ws: workspace?.name ?? '' });
  return <SystemMenu menuKey="sys-customize" title={t('sysPages.customize')} sub={sub} groups={groups}
    scope={<ScopeSwitch state={state} workspaceName={workspace?.name ?? ''} />} />;
}
